import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';

import {
  ShortLinkExportPageDocument,
  ShortLinkExportPageQuery,
  ShortLinkExportPageQueryVariables,
} from 'core/graphql';
import { downloadCsv } from 'core/utils/exportCsv';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import {
  buildShortLinksCsv,
  IShortLinkCsvText,
  SHORT_LINK_CSV_COLUMNS,
  ShortLinkCsvColumn,
  shortLinksCsvFilename,
} from './shortLinkCsv';
import {
  IShortLinkFilters,
  SHORT_LINK_EXPORT_PAGE_SIZE,
  shortLinkChannelLabel,
  ShortLinkSort,
  toShortLinkQueryVariables,
} from './shortLinkModel';
import { IShortLinkRow } from './useShortLinks';

/**
 * Предохранитель выгрузки: 1000 страниц по 100 — сто тысяч ссылок. Больше у
 * юрлица не бывает; цикл без предела при ошибке сервера (одна и та же полная
 * страница в ответ) крутился бы вечно.
 */
const MAX_EXPORT_PAGES = 1000;

export interface IShortLinkExportProgress {
  done: number;
  total: number;
}

/**
 * ТЗ 0080 — выгрузка ссылок в CSV.
 *
 * * «Выгрузить CSV» — ВСЕ найденные по текущим фильтрам и порядку, а не
 *   видимая страница: страницами по 100 (предел сервера) через
 *   `client.query` с `network-only` — выгрузка обязана быть свежей, а не
 *   кэшем часовой давности.
 * * «Выгрузить CSV (N)» — выбранные строки, как они есть на экране.
 *
 * Повтор между страницами (пока листали, кто-то создал ссылку, и порядок
 * «новые сверху» сдвинул выдачу на одну строку) отсекается по id.
 */
export const useShortLinkExport = () => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { addToast } = useToasts();
  const [progress, setProgress] = useState<IShortLinkExportProgress | null>(
    null,
  );
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const notify = useCallback(
    (type: ToastTypes, title: string) => {
      addToast({
        data: { type, title },
        type: GlobalToastTypeNames.ShortLinks,
      });
    },
    [addToast],
  );

  const download = useCallback(
    (rows: ReadonlyArray<IShortLinkRow>, selected: boolean) => {
      const headers = SHORT_LINK_CSV_COLUMNS.reduce(
        (acc, column) => ({ ...acc, [column]: t(`shortLinks.csv.${column}`) }),
        {} as Record<ShortLinkCsvColumn, string>,
      );
      const text: IShortLinkCsvText = {
        headers,
        kind: (kind) => t(`shortLinks.kind.${kind}`, { defaultValue: kind }),
        state: (state) =>
          t(`shortLinks.state.${state}`, { defaultValue: state }),
        channel: (channel) => shortLinkChannelLabel(channel, t),
        contractTitle: t('shortLinks.contractTitle'),
        system: t('shortLinks.createdBySystem'),
      };
      downloadCsv(
        buildShortLinksCsv(rows, text),
        shortLinksCsvFilename(selected),
      );
      notify(
        ToastTypes.success,
        t('shortLinks.export.done', { n: rows.length }),
      );
    },
    [notify, t],
  );

  /** Все найденные по фильтрам — в том порядке, что на экране. */
  const exportFound = useCallback(
    async (filters: IShortLinkFilters, sort: ShortLinkSort, total: number) => {
      if (progress) return;
      setProgress({ done: 0, total });
      const byId = new Map<string, IShortLinkRow>();
      try {
        for (let page = 0; page < MAX_EXPORT_PAGES; page += 1) {
          // Страницы строго по очереди: следующая зависит от того, полна ли
          // текущая, — параллелить нечего.
          // eslint-disable-next-line no-await-in-loop
          const { data } = await client.query<
            ShortLinkExportPageQuery,
            ShortLinkExportPageQueryVariables
          >({
            query: ShortLinkExportPageDocument,
            variables: {
              ...toShortLinkQueryVariables(filters),
              orderBy: sort,
              limit: SHORT_LINK_EXPORT_PAGE_SIZE,
              offset: page * SHORT_LINK_EXPORT_PAGE_SIZE,
            },
            fetchPolicy: 'network-only',
          });
          const rows = data?.shortLinks ?? [];
          rows.forEach((row) => {
            if (!byId.has(row.id)) byId.set(row.id, row);
          });
          if (mountedRef.current) {
            setProgress({
              done: byId.size,
              total: Math.max(total, byId.size),
            });
          }
          if (rows.length < SHORT_LINK_EXPORT_PAGE_SIZE) break;
        }
        download(Array.from(byId.values()), false);
      } catch {
        notify(ToastTypes.error, t('shortLinks.export.failed'));
      } finally {
        if (mountedRef.current) setProgress(null);
      }
    },
    [client, download, notify, progress, t],
  );

  /** Выбранные строки — как они сейчас на экране. */
  const exportSelected = useCallback(
    (rows: ReadonlyArray<IShortLinkRow>) => {
      if (rows.length) download(rows, true);
    },
    [download],
  );

  return { exportFound, exportSelected, progress };
};
